/**
 * useRawMaterials — ViewModel for /manufacturing/raw-materials.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for reads; owns CRUD mutations.
 *
 * Mirrors src/viewmodels/useCategories.ts in shape — M:N picker
 * payload (supplier_ids array per row, suppliers catalogue rides
 * alongside in list `extras`).
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { ProductRef, RawMaterialDto } from '../models/raw-material.model'
import type { UnitDto } from '../models/unit.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/raw-material.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type { ProductRef, RawMaterialDto } from '../models/raw-material.model'
export type { UnitDto } from '../models/unit.model'

export interface CreateRawMaterialInput {
  readonly name: string
  readonly description: string | null
  readonly unit_id: string
  readonly product_id: string
}

export interface UpdateRawMaterialInput {
  readonly name?: string
  readonly description?: string | null
  readonly unit_id?: string
  readonly product_id?: string
}

export interface UseRawMaterialsReturn {
  readonly items: RawMaterialDto[]
  readonly products: ProductRef[]
  readonly units: UnitDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateRawMaterialInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateRawMaterialInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListExtras {
  readonly products: ProductRef[]
  readonly units: UnitDto[]
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: RawMaterialDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly products: ProductRef[]
    readonly units: UnitDto[]
  }
}

const EMPTY_EXTRAS: ListExtras = { products: [], units: [] }

export function useRawMaterials(): UseRawMaterialsReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<RawMaterialDto, ListExtras>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANUFACTURINGRAWMATERIALS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: { products: body.data.products, units: body.data.units }
        }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateRawMaterialInput) => {
      const r = await mutateJson<RawMaterialDto>(
        authedFetch,
        APIROUTES.MANUFACTURINGRAWMATERIALS,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateRawMaterialInput) => {
      const r = await mutateJson<RawMaterialDto>(
        authedFetch,
        fillPath(APIROUTES.MANUFACTURINGRAWMATERIALITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANUFACTURINGRAWMATERIALITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const extras = paged.extras ?? EMPTY_EXTRAS
  return {
    items: paged.items,
    products: extras.products,
    units: extras.units,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove
  }
}
